import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IPremadeQuiz, IQuestion, IQuiz, QuizMode } from '@quizzach/interfaces';
import {
	consoleDev,
	ConsoleStatus,
	fetchData,
	generateQuizStepMap,
	getHalftimeQuestion,
} from '@quizzach/utils';
import { MOCK_QUIZ_PLAY, MOCK_QUIZ_PLAY_VARIANT } from './mocks.config';
import { generateMockQuiz } from './quizPlay.mock';

interface StorageData {
	mode?: QuizMode;
	halftime: boolean;
}

export const useQuiz = () => {
	const { uuid } = useParams<{ uuid: string }>();
	const location = useLocation();
	const navigate = useNavigate();

	const [navigationType, setNavigationType] = useState<
		'hidden' | 'button' | 'plain' | 'feedback'
	>('hidden');
	const [quiz, setQuiz] = useState<IQuiz>();
	const [questions, setQuestions] = useState<IQuestion[]>([]);
	const [premadeDetails, setPremadeDetails] = useState<IPremadeQuiz>();
	const [mode, setMode] = useState<QuizMode>('qqaa');
	const [quizType, setQuizType] = useState<string>('');
	const [halftime, setHalftime] = useState<boolean>(false);
	const [feedback, setFeedback] = useState<{
		level: number | null;
		liked: boolean | null;
	}>({ level: null, liked: null });
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<boolean>(false);

	// ✅ Precalculate steps
	const { stepMap, pathIndexMap } = useMemo(() => {
		return generateQuizStepMap(quiz, mode, halftime);
	}, [quiz, mode, halftime]);

	const currentStepIndex = pathIndexMap?.[location.pathname];
	const currentStep = currentStepIndex ? stepMap[currentStepIndex] : undefined;
	const halfLength = getHalftimeQuestion(quiz) ?? 0;
	const progressSteps = Object.values(stepMap).filter(
		(step) => step.type === 'question' || step.type === 'answer'
	);

	// Walk backward through the real route chain and count only question/answer steps.
	const completedProgressSteps = useMemo(() => {
		if (!currentStep) return 0;

		const visitedRoutes = new Set<string>();
		let completedSteps = 0;
		let stepCursor: typeof currentStep | undefined = currentStep;

		while (stepCursor && !visitedRoutes.has(stepCursor.current)) {
			visitedRoutes.add(stepCursor.current);

			if (stepCursor.type === 'question' || stepCursor.type === 'answer') {
				completedSteps++;
			}

			const previousStepIndex: number | undefined =
				pathIndexMap?.[stepCursor.prev];
			stepCursor =
				previousStepIndex !== undefined
					? stepMap[previousStepIndex]
					: undefined;
		}

		return completedSteps;
	}, [currentStep, pathIndexMap, stepMap]);

	const progressPercentage =
		progressSteps.length > 0
			? Math.max(0, (completedProgressSteps / progressSteps.length) * 100)
			: 0;

	const currentQuestionNumber = useMemo(() => {
		if (!currentStep) return null;
		const match =
			currentStep.current.match(/\/question\/(\d+)/) ||
			currentStep.current.match(/\/answer\/(\d+)/);
		return match ? parseInt(match[1], 10) : null;
	}, [currentStep]);

	const applyQuizData = (data: IQuiz) => {
		setQuiz(data);
		setQuestions(data.questions);
		setQuizType(data.quiz_type);
		setPremadeDetails(data.premade_quiz);

		const hasChapters =
			data.premade_quiz?.chapters && data.premade_quiz.chapters.length > 0;
		// Halftime is offered when there is a valid split point: ≥2 regular
		// questions and, for chaptered quizzes, a chapter boundary near the
		// middle. Puzzle quizzes never use halftime.
		const canHalftime =
			!data.premade_quiz?.puzzle && getHalftimeQuestion(data) !== null;

		const existingStorage = localStorage.getItem(`quiz-${uuid}`);
		const parsedStorage: StorageData = existingStorage
			? JSON.parse(existingStorage)
			: {};

		// Chapters require QQAA mode; halftime keeps the stored preference
		// only when this quiz can actually be split into halves.
		const storageData: StorageData = {
			...parsedStorage,
			halftime: canHalftime ? parsedStorage.halftime ?? false : false,
			mode: hasChapters ? 'qqaa' : parsedStorage.mode ?? 'qqaa',
		};
		setMode(storageData.mode!);
		setHalftime(storageData.halftime);
		localStorage.setItem(`quiz-${uuid}`, JSON.stringify(storageData));
	};

	const getQuizDetails = async () => {
		setLoading(true);
		if (MOCK_QUIZ_PLAY) {
			applyQuizData(
				generateMockQuiz(uuid ?? 'mock-quiz', MOCK_QUIZ_PLAY_VARIANT)
			);
			setLoading(false);
			return;
		}
		try {
			const response = await fetchData<IQuiz>(
				`/api/quizzes/${uuid}`,
				{ method: 'GET' },
				{ authRoute: true }
			);
			if (response.status >= 300) {
				consoleDev('useQuiz', ConsoleStatus.HTTP, response.status);
				setError(true);
			} else {
				consoleDev('useQuiz', ConsoleStatus.SUCC);
				if (response.data) {
					applyQuizData(response.data);
				}
			}
		} catch (err) {
			consoleDev('useQuiz', ConsoleStatus.NET, err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	const sendFeedback = async () => {
		try {
			const response = await fetchData<any>(
				`/api/feedback`,
				{
					method: 'POST',
					body: JSON.stringify({
						...feedback,
						question_uuid: currentQuestionNumber
							? questions[currentQuestionNumber - 1]?.uuid
							: undefined,
					}),
				},
				{ authRoute: true }
			);
			if (response.status >= 300) {
				console.error('Failed to send quiz feedback:', response.status);
				return false;
			}
			consoleDev('feedback', ConsoleStatus.SUCC);
			return true;
		} catch (err) {
			console.error('Failed to send quiz feedback:', err);
			return false;
		}
	};

	const handleNext = () => {
		if (
			currentStep?.type === 'answer' &&
			(feedback.level !== null || feedback.liked !== null)
		) {
			sendFeedback();
		}
		if (currentStep?.next) {
			navigate(currentStep.next);
		}
	};

	const handlePrevious = () => {
		if (currentStep?.prev) {
			navigate(currentStep.prev);
		}
	};

	useEffect(() => {
		getQuizDetails();
	}, [uuid]);

	// Derived values from quiz data
	const featuredQuiz =
		quiz?.premade_quiz && quiz.premade_quiz.author.display_name !== 'quizzach';
	const author = quiz?.premade_quiz?.author.display_name;

	return {
		quiz,
		premadeDetails,
		questions,
		loading,
		error,
		mode,
		halftime,
		quizType,
		feedback,
		navigationType,
		featuredQuiz,
		author,
		handleSetHalftime: (newHalftime: boolean) => {
			setHalftime(newHalftime);
			const quizData = localStorage.getItem(`quiz-${uuid}`);
			if (quizData) {
				const existingData: Partial<StorageData> = JSON.parse(quizData);
				const updatedData = { ...existingData, halftime: newHalftime };
				localStorage.setItem(`quiz-${uuid}`, JSON.stringify(updatedData));
			}
		},
		handleSetMode: (newMode: QuizMode) => {
			setMode(newMode);
			const quizData = localStorage.getItem(`quiz-${uuid}`);
			if (quizData) {
				const existingData: Partial<StorageData> = JSON.parse(quizData);
				const updatedData = { ...existingData, mode: newMode };
				localStorage.setItem(`quiz-${uuid}`, JSON.stringify(updatedData));
			}
		},
		handleNext,
		handlePrevious,
		setFeedback,
		setNavigationType,
		currentQuestionNumber,
		halfLength,
		currentStep,
		progressPercentage,
	};
};
